import { queries } from "@msuite/zero";
import { useZeroQuery } from "@msuite/zero/react";
import type { Enums } from "@msuite/utils/types";

/** The states a file stops in: nothing else will move it without somebody asking. */
const SETTLED: Array<Enums<"fileClassificationState">> = ["linked", "unplaced", "failed"];

/**
 * How far one person's drops have got, live.
 *
 * The rows arrive over the sync path rather than by asking, because nothing the browser does moves
 * them: a worker reads each file, works out where it belongs and writes the answer, and the page
 * would never hear. That is also why the figures below are counts of *files* rather than of bytes —
 * the upload itself reports no progress, and what the person waiting actually wants to know is how
 * many of the eight they dropped have been filed.
 *
 * More than one batch at a time, because a second drop landing while the first is still in flight
 * is a second upload id and the pill reports one figure over both.
 *
 * @param batchIds - The upload ids to follow; an empty list asks for nothing at all.
 * @returns The counts the pill renders, and whether the first answer has arrived.
 */
export function useDropzoneBatches(batchIds: string[]) {
	/** Sync */
	const [classifications, details] = useZeroQuery(
		batchIds.length ? queries.fileClassifications.byBatches({ batchIds }) : false,
	);

	/** Computed */
	const rows = classifications ?? [];
	const settled = rows.filter((row) => SETTLED.includes(row.state)).length;

	/** Return */
	return {
		total: rows.length,
		settled,
		unplaced: rows.filter((row) => row.state === "unplaced").length,
		failed: rows.filter((row) => row.state === "failed").length,
		isLoading: details.type === "unknown",
	};
}
